import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Check, Upload, UserPlus, Database, Users, User, Plus, X, Mail, LogOut } from 'lucide-react';
import LogoIcon from './LogoIcon';
import { useAuth } from '../context/AuthContext';
import { authApi, clientsApi, demoApi, practiceApi } from '../services/api';
import '../pages/Pages.css';

interface OnboardingWizardProps {
  user: { first_name: string; last_name: string; email: string };
  onComplete: () => void;
}

export default function OnboardingWizard({ user, onComplete }: OnboardingWizardProps) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Step 1: Profile
  const [firstName, setFirstName] = useState(user.first_name || '');
  const [lastName, setLastName] = useState(user.last_name || '');
  const [businessName, setBusinessName] = useState('');

  // Step 2: Branding (optional)
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // Step 3: Team Setup
  const [teamType, setTeamType] = useState<'solo' | 'team'>('solo');
  const [practiceName, setPracticeName] = useState('');
  const [inviteEmails, setInviteEmails] = useState<string[]>(['']);
  const [teamCreated, setTeamCreated] = useState(false);
  const [teamCreating, setTeamCreating] = useState(false);
  const [invitesSent, setInvitesSent] = useState(0);

  // Step 4: First client
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientCreated, setClientCreated] = useState(false);
  const [clientError, setClientError] = useState('');

  // Step 5: Demo data
  const [loadDemo, setLoadDemo] = useState(true);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoResult, setDemoResult] = useState<string | null>(null);

  const totalSteps = 6;

  const handleSkipAll = async () => {
    setSaving(true);
    try {
      const { subscriptionsApi } = await import('../services/api');
      await subscriptionsApi.completeOnboarding({ onboarding_completed: true });
    } catch { /* ignore */ }
    finally {
      setSaving(false);
      onComplete();
    }
  };

  const handleNext = async () => {
    if (step === 1 && (firstName || lastName || businessName)) {
      try {
        await authApi.updateProfile({
          first_name: firstName || undefined,
          last_name: lastName || undefined,
          business_name: businessName || undefined,
        });
      } catch { /* continue anyway */ }
    }
    if (step < totalSteps - 1) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleTeamSetup = async () => {
    if (teamType === 'solo') {
      setStep(step + 1);
      return;
    }
    if (!practiceName.trim()) return;

    setTeamCreating(true);
    try {
      await practiceApi.create({ name: practiceName.trim() });
      setTeamCreated(true);

      // Send invitations
      const validEmails = inviteEmails.filter(e => e.trim() && e.includes('@'));
      let sent = 0;
      for (const email of validEmails) {
        try {
          await practiceApi.invite({ email: email.trim(), role: 'coach' });
          sent++;
        } catch { /* continue on error */ }
      }
      setInvitesSent(sent);
    } catch { /* ignore */ }
    finally { setTeamCreating(false); }
  };

  const handleCreateClient = async () => {
    if (!clientName.trim()) return;
    setSaving(true);
    setClientError('');
    try {
      await clientsApi.create({
        first_name: clientName.split(' ')[0] || clientName,
        last_name: clientName.split(' ').slice(1).join(' ') || '',
        email: clientEmail || undefined,
      });
      setClientCreated(true);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Fehler beim Anlegen';
      setClientError(typeof msg === 'string' ? msg : 'Fehler beim Anlegen');
    }
    finally { setSaving(false); }
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      if (loadDemo) {
        setDemoLoading(true);
        try {
          const res = await demoApi.seed();
          if (res.data?.seeded) {
            setDemoResult(`${res.data.clients} Klienten, ${res.data.cases} Fälle, ${res.data.invoices} Rechnungen geladen`);
          }
        } catch { /* continue anyway */ }
        setDemoLoading(false);
      }
      const { subscriptionsApi } = await import('../services/api');
      await subscriptionsApi.completeOnboarding({ onboarding_completed: true });
    } catch { /* ignore */ }
    finally {
      setSaving(false);
      onComplete();
    }
  };

  const addInviteEmail = () => {
    if (inviteEmails.length < 10) {
      setInviteEmails([...inviteEmails, '']);
    }
  };

  const removeInviteEmail = (index: number) => {
    setInviteEmails(inviteEmails.filter((_, i) => i !== index));
  };

  const updateInviteEmail = (index: number, value: string) => {
    const updated = [...inviteEmails];
    updated[index] = value;
    setInviteEmails(updated);
  };

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-wrapper">
      <div className="onboarding-card" style={{ position: 'relative' }}>
        {/* Skip / Close Button */}
        <button
          onClick={handleSkipAll}
          disabled={saving}
          title="Einrichtung überspringen"
          style={{
            position: 'absolute', top: 16, right: 16,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-muted)', display: 'flex', alignItems: 'center',
            gap: 4, fontSize: '0.8rem', padding: '4px 8px', borderRadius: 6,
          }}
        >
          <X size={16} />
        </button>
        {/* Step Indicator */}
        <div className="onboarding-steps">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`onboarding-step ${i <= step ? 'onboarding-step-active' : ''}`}
              style={{ width: i === step ? 32 : 10 }}
            />
          ))}
        </div>

        {/* Step 0: Welcome */}
        {step === 0 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ margin: '0 auto 24px', display: 'flex', justifyContent: 'center' }}>
              <LogoIcon size={64} variant="theme" />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.5rem', color: 'var(--text)' }}>Willkommen bei CoachDesk365</h2>
            <p className="text-muted" style={{ margin: '0 0 32px', fontSize: '0.95rem' }}>
              Lassen Sie uns Ihr Coaching-Profil einrichten.
              <br />Das dauert nur 2 Minuten.
            </p>
            <button className="btn btn-primary" onClick={handleNext} style={{ padding: '12px 32px' }}>
              Los geht's <ChevronRight size={18} style={{ marginLeft: 4 }} />
            </button>
            <p style={{ marginTop: 16 }}>
              <button
                onClick={handleSkipAll}
                disabled={saving}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', fontSize: '0.85rem', textDecoration: 'underline',
                }}
              >
                Später einrichten — direkt zum Dashboard
              </button>
            </p>
          </div>
        )}

        {/* Step 1: Profile */}
        {step === 1 && (
          <div>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.3rem' }}>Ihr Profil</h2>
            <p className="text-muted text-sm" style={{ margin: '0 0 24px' }}>
              Dies wird in Rechnungen und auf Dokumenten angezeigt.
            </p>
            <div className="form-row" style={{ marginBottom: 16 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Vorname</label>
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Nachname</label>
                <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 24 }}>
              <label>Firma / Praxisname</label>
              <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="z.B. AC Leadership" />
            </div>
            <div className="flex-between">
              <button className="btn btn-secondary" onClick={handleBack}><ChevronLeft size={16} /> Zurück</button>
              <button className="btn btn-primary" onClick={handleNext}>Weiter <ChevronRight size={16} /></button>
            </div>
          </div>
        )}

        {/* Step 2: Branding */}
        {step === 2 && (
          <div>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.3rem' }}>Branding</h2>
            <p className="text-muted text-sm" style={{ margin: '0 0 24px' }}>
              Ihr Logo wird auf Rechnungen und Dokumenten angezeigt. (Optional)
            </p>
            <div
              className="onboarding-upload"
              style={{ marginBottom: 24, background: logoPreview ? 'transparent' : undefined }}
              onClick={() => document.getElementById('logo-upload')?.click()}
            >
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" style={{ maxHeight: 80, maxWidth: '100%' }} />
              ) : (
                <>
                  <Upload size={32} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
                  <p className="text-muted text-sm" style={{ margin: 0 }}>Logo hochladen (PNG, JPG)</p>
                </>
              )}
              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = () => setLogoPreview(reader.result as string);
                    reader.readAsDataURL(file);
                  }
                }}
              />
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: 16,
              background: 'var(--bg)', borderRadius: 'var(--radius)', marginBottom: 24,
            }}>
              <div style={{ width: 24, height: 24, borderRadius: 6, background: 'var(--primary)' }} />
              <span className="text-muted text-sm">Ihre Markenfarbe</span>
            </div>
            <div className="flex-between">
              <button className="btn btn-secondary" onClick={handleBack}><ChevronLeft size={16} /> Zurück</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => setStep(step + 1)}>Überspringen</button>
                <button className="btn btn-primary" onClick={handleNext}>Weiter <ChevronRight size={16} /></button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Team Setup */}
        {step === 3 && (
          <div>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.3rem' }}>Arbeiten Sie im Team?</h2>
            <p className="text-muted text-sm" style={{ margin: '0 0 24px' }}>
              Richten Sie Ihre Praxis ein und laden Sie Kollegen ein — oder arbeiten Sie zunächst allein.
            </p>

            {!teamCreated ? (
              <>
                {/* Solo / Team Selection */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
                  <button
                    onClick={() => setTeamType('solo')}
                    style={{
                      flex: 1, padding: '16px 12px', borderRadius: 'var(--radius)',
                      border: `2px solid ${teamType === 'solo' ? 'var(--primary)' : 'var(--border)'}`,
                      background: teamType === 'solo' ? 'rgba(var(--primary-rgb, 180,130,120), 0.08)' : 'var(--bg)',
                      cursor: 'pointer', textAlign: 'center',
                    }}
                  >
                    <User size={24} style={{ color: teamType === 'solo' ? 'var(--primary)' : 'var(--text-muted)', marginBottom: 8 }} />
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text)' }}>Solo-Coach</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 }}>Ich arbeite allein</div>
                  </button>
                  <button
                    onClick={() => {
                      setTeamType('team');
                      if (!practiceName && businessName) setPracticeName(businessName);
                    }}
                    style={{
                      flex: 1, padding: '16px 12px', borderRadius: 'var(--radius)',
                      border: `2px solid ${teamType === 'team' ? 'var(--primary)' : 'var(--border)'}`,
                      background: teamType === 'team' ? 'rgba(var(--primary-rgb, 180,130,120), 0.08)' : 'var(--bg)',
                      cursor: 'pointer', textAlign: 'center',
                    }}
                  >
                    <Users size={24} style={{ color: teamType === 'team' ? 'var(--primary)' : 'var(--text-muted)', marginBottom: 8 }} />
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text)' }}>Team / Praxis</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 }}>Wir arbeiten zusammen</div>
                  </button>
                </div>

                {teamType === 'solo' && (
                  <div style={{
                    padding: '12px 16px', background: 'var(--bg)', borderRadius: 'var(--radius)',
                    fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 24,
                  }}>
                    Sie können jederzeit in den Einstellungen ein Team einrichten und Kollegen einladen.
                  </div>
                )}

                {teamType === 'team' && (
                  <>
                    <div className="form-group" style={{ marginBottom: 16 }}>
                      <label htmlFor="ob-practice-name">Praxisname</label>
                      <input
                        id="ob-practice-name"
                        type="text"
                        value={practiceName}
                        onChange={(e) => setPracticeName(e.target.value)}
                        placeholder={businessName || 'z.B. Coaching-Praxis Müller'}
                        autoFocus
                      />
                    </div>

                    <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8, display: 'block', color: 'var(--text)' }}>
                      <Mail size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                      Kollegen einladen (optional)
                    </label>
                    {inviteEmails.map((email, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <input
                          value={email}
                          onChange={(e) => updateInviteEmail(i, e.target.value)}
                          placeholder="coach@beispiel.de"
                          type="email"
                          style={{ flex: 1 }}
                        />
                        {inviteEmails.length > 1 && (
                          <button
                            onClick={() => removeInviteEmail(i)}
                            className="btn btn-secondary"
                            style={{ padding: '6px 8px', minWidth: 'auto' }}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    {inviteEmails.length < 10 && (
                      <button
                        onClick={addInviteEmail}
                        className="btn btn-secondary"
                        style={{ fontSize: '0.8rem', padding: '4px 12px', marginBottom: 16 }}
                      >
                        <Plus size={14} /> Weitere Person
                      </button>
                    )}
                  </>
                )}

                <div className="flex-between" style={{ marginTop: 8 }}>
                  <button className="btn btn-secondary" onClick={handleBack}><ChevronLeft size={16} /> Zurück</button>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {teamType === 'solo' && (
                      <button className="btn btn-primary" onClick={() => setStep(step + 1)}>
                        Weiter <ChevronRight size={16} />
                      </button>
                    )}
                    {teamType === 'team' && (
                      <>
                        <button className="btn btn-secondary" onClick={() => setStep(step + 1)}>
                          Überspringen
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={handleTeamSetup}
                          disabled={teamCreating || !practiceName.trim()}
                        >
                          {teamCreating ? 'Wird eingerichtet...' : 'Praxis erstellen'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="onboarding-success" style={{ marginBottom: 16 }}>
                  <Check size={24} style={{ color: 'var(--success)', marginBottom: 4 }} />
                  <p style={{ margin: 0, color: 'var(--success)', fontWeight: 600 }}>
                    Praxis "{practiceName}" wurde erstellt!
                  </p>
                </div>
                {invitesSent > 0 && (
                  <div style={{
                    padding: '10px 16px', background: 'var(--bg)', borderRadius: 'var(--radius)',
                    fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 24, textAlign: 'center',
                  }}>
                    {invitesSent} Einladung{invitesSent > 1 ? 'en' : ''} versendet
                  </div>
                )}
                <div className="flex-between">
                  <span />
                  <button className="btn btn-primary" onClick={() => setStep(step + 1)}>
                    Weiter <ChevronRight size={16} />
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 4: First Client */}
        {step === 4 && (
          <div>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.3rem' }}>Erster Klient</h2>
            <p className="text-muted text-sm" style={{ margin: '0 0 24px' }}>
              Fügen Sie Ihren ersten Klienten hinzu — oder machen Sie das später.
            </p>
            {!clientCreated ? (
              <>
                <div className="form-group">
                  <label>Name des Klienten</label>
                  <input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="z.B. Max Mustermann" />
                </div>
                <div className="form-group" style={{ marginBottom: 24 }}>
                  <label>E-Mail (optional)</label>
                  <input value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="max@beispiel.de" />
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateClient}
                  disabled={saving || !clientName.trim()}
                  style={{ width: '100%', marginBottom: 12 }}
                >
                  <UserPlus size={16} /> {saving ? 'Wird angelegt...' : 'Klient hinzufügen'}
                </button>
                {clientError && (
                  <p style={{ color: 'var(--danger, #dc2626)', fontSize: '0.82rem', margin: '0 0 8px', textAlign: 'center' }}>
                    {clientError}
                  </p>
                )}
              </>
            ) : (
              <div className="onboarding-success" style={{ marginBottom: 24 }}>
                <Check size={24} style={{ color: 'var(--success)', marginBottom: 4 }} />
                <p style={{ margin: 0, color: 'var(--success)', fontWeight: 600 }}>{clientName} wurde angelegt!</p>
              </div>
            )}
            <div className="flex-between">
              <button className="btn btn-secondary" onClick={handleBack}><ChevronLeft size={16} /> Zurück</button>
              <div style={{ display: 'flex', gap: 8 }}>
                {!clientCreated && (
                  <button className="btn btn-secondary" onClick={() => setStep(step + 1)}>Später</button>
                )}
                {clientCreated && (
                  <button className="btn btn-primary" onClick={handleNext}>Weiter <ChevronRight size={16} /></button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Done */}
        {step === 5 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', margin: '0 auto 24px',
              background: 'rgba(var(--success-rgb, 91, 140, 90), 0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Check size={32} style={{ color: 'var(--success)' }} />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.5rem' }}>Alles eingerichtet!</h2>
            <p className="text-muted" style={{ margin: '0 0 16px', fontSize: '0.95rem' }}>
              Ihr CoachDesk365-Konto ist bereit.
            </p>
            <div style={{
              padding: '12px 20px', background: 'var(--bg)', borderRadius: 'var(--radius)',
              display: 'inline-block', marginBottom: 24,
            }}>
              <span style={{ fontSize: '0.85rem', color: 'var(--primary)', fontWeight: 600 }}>
                Free Trial — 14 Tage kostenlos
              </span>
            </div>

            <label
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
                background: 'var(--bg)', borderRadius: 'var(--radius)', cursor: 'pointer',
                marginBottom: 24, textAlign: 'left',
              }}
            >
              <input
                type="checkbox"
                checked={loadDemo}
                onChange={(e) => setLoadDemo(e.target.checked)}
                style={{ width: 18, height: 18, accentColor: 'var(--primary)', flexShrink: 0 }}
              />
              <div>
                <span style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text)' }}>
                  <Database size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />
                  Demo-Daten laden
                </span>
                <br />
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  Beispiel-Klienten, Fälle und Rechnungen zum Ausprobieren. Jederzeit löschbar.
                </span>
              </div>
            </label>

            {demoResult && (
              <div style={{ marginBottom: 16, color: 'var(--success)', fontSize: '0.85rem', fontWeight: 500 }}>
                {demoResult}
              </div>
            )}

            <button
              className="btn btn-primary"
              onClick={handleFinish}
              disabled={saving || demoLoading}
              style={{ padding: '12px 40px' }}
            >
              {demoLoading ? 'Demo-Daten werden geladen...' : saving ? 'Wird gespeichert...' : 'Zum Dashboard'}
            </button>
          </div>
        )}
        </div>
        <button
          onClick={() => { logout(); navigate('/'); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, margin: '16px auto 0',
            background: 'none', border: 'none', color: 'var(--text-muted, #94a3b8)',
            cursor: 'pointer', fontSize: '0.82rem',
          }}
        >
          <LogOut size={14} />
          Abmelden und zur Startseite
        </button>
      </div>
    </div>
  );
}
